import type { FC, ReactNode } from "react";
import { useLayoutEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { authController } from "../AppController";
import { circadiaEngine } from "../circadiaEngineInstance";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import {
  clearAuth,
  selectIsAuthenticated,
  setAuth,
} from "../redux/Auth/auth.slice";
import type { AppDispatch } from "../redux";
import type { RootState } from "../redux/rootReducer";
import { fieldTeamMemberFromIdToken } from "../util/fieldTeamMemberFromIdToken";
import { ROUTES } from "./mobile/routes";

export { selectIsAuthenticated };

/**
 * One in-flight native token check so concurrent RequireAuth mounts share work.
 * Cleared when done so each navigation can re-verify (token may have expired).
 */
let nativeSessionCheckPromise: Promise<void> | null = null;

function validateNativeAuthSession(dispatch: AppDispatch): Promise<void> {
  if (!circadiaEngine.isWindowCircadia()) {
    return Promise.resolve();
  }
  if (!nativeSessionCheckPromise) {
    nativeSessionCheckPromise = (async () => {
      const token = await authController.getAuthenticationToken();
      if (token) {
        const member = fieldTeamMemberFromIdToken(token, "");
        if (member) {
          dispatch(setAuth({ fieldTeamMember: member, token }));
          return;
        }
      }
      dispatch(clearAuth());
    })().finally(() => {
      nativeSessionCheckPromise = null;
    });
  }
  return nativeSessionCheckPromise;
}

export const selectCompletedSteps = (state: RootState) =>
  state.setup.completedSteps;

export const selectCurrentPatientId = (state: RootState) =>
  state.setup.currentPatientId;

export const RequireAuth: FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  /** On native, false until `getAuthenticationToken` has been checked (do not trust persisted Redux alone). */
  const [nativeSessionChecked, setNativeSessionChecked] = useState(
    () => !circadiaEngine.isWindowCircadia()
  );

  useLayoutEffect(() => {
    if (!circadiaEngine.isWindowCircadia()) {
      return;
    }
    let cancelled = false;
    void validateNativeAuthSession(dispatch).finally(() => {
      if (!cancelled) {
        setNativeSessionChecked(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (!nativeSessionChecked) {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center p-8"
        aria-busy="true"
      >
        <div
          className="h-10 w-10 animate-spin rounded-full border-4 border-[#1B2B6B] border-t-transparent"
          role="presentation"
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  return <>{children}</>;
};

interface RequireStepProps {
  children: ReactNode;
  /** Prior step index that must appear in `completedSteps` (e.g. 1 for setup step 2). */
  requiredStep: number;
}

export const RequireStep: FC<RequireStepProps> = ({
  children,
  requiredStep,
}) => {
  const completedSteps = useAppSelector(selectCompletedSteps);
  const patientIdFromStore = useAppSelector(selectCurrentPatientId);
  const { patientId: patientIdParam } = useParams<{ patientId: string }>();
  const patientId = patientIdParam ?? patientIdFromStore ?? "new";

  if (!completedSteps.includes(requiredStep)) {
    return (
      <Navigate
        to={`/setup/${encodeURIComponent(patientId)}/1`}
        replace
      />
    );
  }
  return <>{children}</>;
};
